(ns arthur.flow.measure.anchor
  "Stage 3, the anchor: the rigid transform per frame, and the space every other
  measurement is taken in.

  The fit is knob-free, deliberately. Smoothing its four parameters is stage 4 —
  `arthur.flow.condition` — because `anchor avg` is a knob and the rest of measure
  is not, and because a residual that moved when a smoothing slider moved would
  report the footage as unstabilisable on account of a setting.

  `makeXform` is NOT here, and is not being ported. It centres on the face oval's
  bounding box and zooms until the face is 80% of the raster height, so every
  vertex it touches carries a cropping decision made once, at analysis time, from
  one frame's landmarks. Geometry is stored in the node's own local space and the
  framing is a transform on a node; see \"What space geometry is in\" in
  docs/animation-model.md. So the face oval is not measured here either — its only
  consumers in the prototype were that transform and the placeholder plate
  outline, and the plate outline belongs to painting."
  (:require [arthur.domain.geom :as geom]
            [arthur.domain.landmarks :as lm]))

(defn pick
  "Landmarks `idx` out of one dense `frame`, converted to an ISOTROPIC space.

  MediaPipe normalises x by image WIDTH and y by image HEIGHT, so its normalised
  space is anisotropic: for a 1080x1920 frame, one unit of x is 1080px and one
  unit of y is 1920px. Treating those as comparable stretches everything
  horizontally by H/W, and worse, makes fit-similarity fit a \"rotation\" in a
  sheared space, so head roll comes out subtly wrong as well.

  Multiplying x by aspect = W/H converts to an isotropic space whose unit is one
  image height, so equal numbers mean equal pixels. Everything downstream -
  Procrustes, the similarity fit, the raster transform - depends on that."
  [frame idx aspect]
  (mapv (fn [i] (let [p (nth frame i)] {:x (* (:x p) aspect) :y (:y p)})) idx))

(defn residuals
  "RMS misfit per frame, in the isotropic space's units — one image height.

  Taken against the transforms it is HANDED rather than against a fit of its own,
  which is what let the parity diff assert on exactly what the prototype handed
  it while that diff existed. The prototype took the residual against the
  SMOOTHED transforms, which folds the smoothing
  error into a number whose whole job is to say whether the footage is
  stabilisable at all; `fit` takes it against the raw fit instead."
  [ref rigid tfs]
  (mapv (fn [rig tf] (geom/fit-residual tf rig ref)) rigid tfs))

(defn fit
  "Dense landmarks -> the rigid fit of every frame onto the shot's mean pose.

  The reference is the Procrustes MEAN configuration over the shot, not frame
  zero, so no single frame's idiosyncrasies get baked into every other frame."
  [{:keys [aspect]} {:keys [dense]}]
  (let [rigid (mapv #(pick % lm/RIGID aspect) dense)
        ref   (geom/procrustes-mean rigid)
        tfs   (mapv #(geom/fit-similarity % ref) rigid)]
    {:ref        ref
     ;; Rigid landmarks in IMAGE space: the head-pose signal. Frame removal is
     ;; decided from head motion, not from the mouth, so this has to survive the
     ;; fit rather than being consumed by it.
     :rigid      rigid
     :transforms tfs
     ;; Residual rises with out-of-plane rotation, which no 2D similarity can
     ;; remove. High values mean this section wants a different head plate.
     :residual   (residuals ref rigid tfs)}))
